import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';
import type { Notification } from '@/types/notifications';

/**
 * NotificationDeliveryService
 * 
 * Handles real-time delivery of notifications via Supabase subscriptions.
 * Manages multi-device sync, delivery confirmation, and cross-tab communication.
 */

type NotificationHandler = (notification: Notification) => void;
type ReadStatusHandler = (notificationId: string, isRead: boolean) => void;
type DeleteHandler = (notificationId: string) => void;

interface DeliveryStats {
  delivered_at: string;
  client_id: string;
  device_info?: {
    userAgent: string;
    platform: string;
  };
}

class NotificationDeliveryService {
  private channel: RealtimeChannel | null = null;
  private organizationId: string | null = null;
  private userId: string | null = null;
  private clientId: string;
  private broadcastChannel: BroadcastChannel | null = null;
  
  // Event handlers
  private notificationHandlers: Set<NotificationHandler> = new Set();
  private readStatusHandlers: Set<ReadStatusHandler> = new Set();
  private deleteHandlers: Set<DeleteHandler> = new Set();
  
  // Delivery tracking
  private deliveredNotifications: Set<string> = new Set();
  
  constructor() {
    // Generate unique client ID for this browser tab
    this.clientId = this.generateClientId();
    
    // Cross-tab communication via BroadcastChannel
    this.setupCrossTabSync();
  }
  
  /**
   * Initialize real-time subscriptions for a user/organization
   */
  async initialize(userId: string, organizationId: string): Promise<void> {
    // Cleanup existing subscription
    await this.cleanup();
    
    this.userId = userId;
    this.organizationId = organizationId;
    
    // Subscribe to notifications for this organization
    this.channel = supabase
      .channel(`notifications:${organizationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `organization_id=eq.${organizationId}`,
        },
        (payload) => {
          this.handleNewNotification(payload.new as Notification);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `organization_id=eq.${organizationId}`,
        },
        (payload) => {
          this.handleNotificationUpdate(payload.new as Notification);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'notifications',
          filter: `organization_id=eq.${organizationId}`,
        },
        (payload) => {
          this.handleNotificationDelete(payload.old.id);
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('[NotificationDelivery] ✅ Real-time subscription active');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('[NotificationDelivery] ❌ Subscription error, attempting reconnect...');
          this.attemptReconnect();
        }
      });
  }
  
  /**
   * Cleanup subscriptions
   */
  async cleanup(): Promise<void> {
    if (this.channel) {
      await supabase.removeChannel(this.channel);
      this.channel = null;
    }
    
    // Clean up BroadcastChannel
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }
    
    this.organizationId = null;
    this.userId = null;
    this.deliveredNotifications.clear();
  }
  
  /**
   * Register handler for new notifications
   */
  onNotification(handler: NotificationHandler): () => void {
    this.notificationHandlers.add(handler);
    return () => this.notificationHandlers.delete(handler);
  }
  
  /**
   * Register handler for read status changes
   */
  onReadStatusChange(handler: ReadStatusHandler): () => void {
    this.readStatusHandlers.add(handler);
    return () => this.readStatusHandlers.delete(handler);
  }
  
  /**
   * Register handler for notification deletions
   */
  onDelete(handler: DeleteHandler): () => void {
    this.deleteHandlers.add(handler);
    return () => this.deleteHandlers.delete(handler);
  }
  
  /**
   * Confirm delivery of a notification
   */
  async confirmDelivery(notificationId: string): Promise<void> {
    if (this.deliveredNotifications.has(notificationId)) {
      return; // Already confirmed
    }
    
    const deliveryStats: DeliveryStats = {
      delivered_at: new Date().toISOString(),
      client_id: this.clientId,
      device_info: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
      },
    };
    
    try {
      const { error } = await supabase
        .from('notifications')
        .update({
          delivered_at: deliveryStats.delivered_at,
          delivery_stats: deliveryStats,
        })
        .eq('id', notificationId)
        .is('delivered_at', null); // Only update if not already delivered
      
      if (error) throw error;
      
      this.deliveredNotifications.add(notificationId);
      
      // Log to history
      await this.logToHistory(notificationId, 'delivered', { deliveryStats });
    } catch (error) {
      console.error('[NotificationDelivery] Failed to confirm delivery:', error);
    }
  }
  
  /**
   * Mark notification as read
   */
  async markAsRead(notificationId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({
          is_read: true,
          read_at: new Date().toISOString(),
        })
        .eq('id', notificationId);
      
      if (error) throw error;
      
      // Broadcast to other tabs
      this.broadcastReadStatus(notificationId, true);
      
      // Log to history
      await this.logToHistory(notificationId, 'read');
    } catch (error) {
      console.error('[NotificationDelivery] Failed to mark as read:', error);
      throw error;
    }
  }
  
  /**
   * Mark notification as unread
   */
  async markAsUnread(notificationId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({
          is_read: false,
          read_at: null,
        })
        .eq('id', notificationId);
      
      if (error) throw error;
      
      // Broadcast to other tabs
      this.broadcastReadStatus(notificationId, false);
      
      // Log to history
      await this.logToHistory(notificationId, 'unread');
    } catch (error) {
      console.error('[NotificationDelivery] Failed to mark as unread:', error);
      throw error;
    }
  }
  
  /**
   * Dismiss/delete a notification
   */
  async dismiss(notificationId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);
      
      if (error) throw error;
      
      // Log to history before deletion
      await this.logToHistory(notificationId, 'dismissed');
      
      // Broadcast to other tabs
      this.broadcastDeletion(notificationId);
    } catch (error) {
      console.error('[NotificationDelivery] Failed to dismiss notification:', error);
      throw error;
    }
  }
  
  /**
   * Handle new notification from real-time subscription
   */
  private async handleNewNotification(notification: Notification): Promise<void> {
    console.log('[NotificationDelivery] 📬 New notification received:', notification.id);
    
    // Confirm delivery
    await this.confirmDelivery(notification.id);
    
    // Notify all handlers
    this.notificationHandlers.forEach((handler) => {
      try {
        handler(notification);
      } catch (error) {
        console.error('[NotificationDelivery] Handler error:', error);
      }
    });
  }
  
  /**
   * Handle notification update (e.g., marked as read)
   */
  private handleNotificationUpdate(notification: Notification): void {
    console.log('[NotificationDelivery] 🔄 Notification updated:', notification.id);
    
    // If read status changed, notify handlers
    this.readStatusHandlers.forEach((handler) => {
      try {
        handler(notification.id, notification.is_read);
      } catch (error) {
        console.error('[NotificationDelivery] Read status handler error:', error);
      }
    });
  }
  
  /**
   * Handle notification deletion
   */
  private handleNotificationDelete(notificationId: string): void {
    console.log('[NotificationDelivery] 🗑️ Notification deleted:', notificationId);
    
    this.deliveredNotifications.delete(notificationId);
    
    this.deleteHandlers.forEach((handler) => {
      try {
        handler(notificationId);
      } catch (error) {
        console.error('[NotificationDelivery] Delete handler error:', error);
      }
    });
  }
  
  /**
   * Log action to notification_history
   */
  private async logToHistory(
    notificationId: string,
    action: string,
    metadata?: Record<string, unknown>
  ): Promise<void> {
    try {
      await supabase.from('notification_history').insert({
        notification_id: notificationId,
        user_id: this.userId,
        action,
        metadata: metadata || {},
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      // Don't throw - history logging is best-effort
      console.warn('[NotificationDelivery] Failed to log to history:', error);
    }
  }
  
  /**
   * Generate unique client ID for this browser tab
   */
  private generateClientId(): string {
    return `client_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }
  
  /**
   * Setup cross-tab synchronization via BroadcastChannel
   */
  private setupCrossTabSync(): void {
    if (typeof BroadcastChannel === 'undefined') {
      console.warn('[NotificationDelivery] BroadcastChannel not supported, cross-tab sync disabled');
      return;
    }
    
    // Close existing channel first to prevent leaks
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
    }
    
    this.broadcastChannel = new BroadcastChannel('notification-sync');
    
    this.broadcastChannel.onmessage = (event) => {
      const { type, payload } = event.data;
      
      switch (type) {
        case 'read-status':
          this.readStatusHandlers.forEach((handler) => {
            handler(payload.notificationId, payload.isRead);
          });
          break;
        
        case 'delete':
          this.deleteHandlers.forEach((handler) => {
            handler(payload.notificationId);
          });
          break;
      }
    };
  }
  
  /**
   * Broadcast read status to other tabs
   */
  private broadcastReadStatus(notificationId: string, isRead: boolean): void {
    try {
      const bc = new BroadcastChannel('notification-sync');
      bc.postMessage({
        type: 'read-status',
        payload: { notificationId, isRead },
      });
      bc.close();
    } catch (error) {
      // Ignore broadcast errors
    }
  }
  
  /**
   * Broadcast deletion to other tabs
   */
  private broadcastDeletion(notificationId: string): void {
    try {
      const bc = new BroadcastChannel('notification-sync');
      bc.postMessage({
        type: 'delete',
        payload: { notificationId },
      });
      bc.close();
    } catch (error) {
      // Ignore broadcast errors
    }
  }
  
  /**
   * Attempt to reconnect after subscription error
   */
  private reconnectAttempts = 0;
  private static readonly MAX_RECONNECT_ATTEMPTS = 3;

  private async attemptReconnect(): Promise<void> {
    if (!this.userId || !this.organizationId) return;
    if (this.reconnectAttempts >= NotificationDeliveryService.MAX_RECONNECT_ATTEMPTS) {
      console.warn('[NotificationDelivery] Max reconnect attempts reached, giving up. Realtime may not be enabled for notifications table.');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(2000 * Math.pow(2, this.reconnectAttempts - 1), 30000);
    console.log(`[NotificationDelivery] 🔄 Reconnect attempt ${this.reconnectAttempts}/${NotificationDeliveryService.MAX_RECONNECT_ATTEMPTS} in ${delay}ms...`);

    await new Promise((resolve) => setTimeout(resolve, delay));

    try {
      await this.initialize(this.userId, this.organizationId);
      this.reconnectAttempts = 0; // Reset on success
    } catch (error) {
      console.error('[NotificationDelivery] Reconnect failed:', error);
      this.attemptReconnect();
    }
  }
}

// Singleton instance
export const notificationDeliveryService = new NotificationDeliveryService();
